import type { IconName } from "../components/Icon.js";
import type { SidebarSection } from "../workspace/Sidebar.js";

export type FieldType =
  | "text"
  | "textarea"
  | "select"
  | "date"
  | "scene-ref"
  | "location-ref";

export interface FieldSpec {
  key: string;
  label: string;
  type: FieldType;
  options?: string[];
  rows?: number;
  placeholder?: string;
  /** Render on its own full-width row (default true for textareas). */
  full?: boolean;
  hint?: string;
}

export interface CollectionConfig {
  section: SidebarSection;
  /** API path segment. */
  path: string;
  label: string;
  singular: string;
  icon: IconName;
  /** The field used as the item's display title. */
  titleField: string;
  /** Default field values for a newly created item. */
  newItem: Record<string, unknown>;
  /** Secondary line shown under the title in the list. */
  subtitleField?: string;
  fields: FieldSpec[];
  /** Show the "mentions" panel (characters only). */
  showMentions?: boolean;
  /** Order items manually (timeline). */
  reorderable?: boolean;
  /** Show a portrait + upload controls (characters); stored on `imageData`. */
  showImage?: boolean;
}

const STORY_BIBLE_CATEGORIES = [
  "World rules",
  "Important facts",
  "Themes",
  "Backstory",
  "Organizations",
  "Objects",
  "Historical events",
  "Terminology",
  "Other"
];

const PLOT_STATUSES = ["planned", "active", "foreshadowed", "resolved", "abandoned"];

export const COLLECTION_CONFIGS: Record<string, CollectionConfig> = {
  characters: {
    section: "characters",
    path: "characters",
    label: "Characters",
    singular: "Character",
    icon: "users",
    titleField: "name",
    newItem: { name: "New Character" },
    subtitleField: "occupation",
    showMentions: true,
    showImage: true,
    fields: [
      { key: "fullName", label: "Full name", type: "text" },
      { key: "tags", label: "Role tags", type: "text", placeholder: "Protagonist, Streamer, Gamer…", hint: "Shown as chips on the gallery card" },
      { key: "nicknames", label: "Nicknames", type: "text" },
      { key: "pronouns", label: "Pronouns", type: "text" },
      { key: "age", label: "Age", type: "text" },
      { key: "birthday", label: "Birthday", type: "text" },
      { key: "status", label: "Status", type: "text", placeholder: "Alive, deceased…" },
      { key: "occupation", label: "Occupation", type: "text" },
      { key: "firstAppearanceSceneId", label: "First appearance", type: "scene-ref" },
      { key: "appearance", label: "Appearance", type: "textarea" },
      { key: "personality", label: "Personality", type: "textarea" },
      { key: "background", label: "Background", type: "textarea" },
      { key: "motivations", label: "Motivations", type: "textarea" },
      { key: "fears", label: "Fears", type: "textarea" },
      { key: "relationships", label: "Relationships", type: "textarea" },
      { key: "secrets", label: "Secrets", type: "textarea" },
      { key: "characterArc", label: "Character arc", type: "textarea" },
      { key: "notes", label: "Notes", type: "textarea" }
    ]
  },
  locations: {
    section: "locations",
    path: "locations",
    label: "Locations",
    singular: "Location",
    icon: "map",
    titleField: "name",
    newItem: { name: "New Location" },
    subtitleField: "type",
    fields: [
      { key: "type", label: "Type", type: "text", placeholder: "City, building, planet…" },
      { key: "aliases", label: "Aliases", type: "text", placeholder: "the bar, the club — other names used in the prose" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "visualDetails", label: "Visual details", type: "textarea" },
      { key: "importantEvents", label: "Important events", type: "textarea" },
      { key: "associatedCharacters", label: "Associated characters", type: "textarea" },
      { key: "notes", label: "Notes", type: "textarea" }
    ]
  },
  storyBible: {
    section: "storyBible",
    path: "story-bible",
    label: "Story Bible",
    singular: "Entry",
    icon: "book",
    titleField: "title",
    newItem: { title: "New Entry", category: "World rules" },
    subtitleField: "category",
    fields: [
      { key: "category", label: "Category", type: "select", options: STORY_BIBLE_CATEGORIES },
      { key: "tags", label: "Tags", type: "text", placeholder: "comma, separated, tags" },
      { key: "content", label: "Content", type: "textarea", rows: 12 }
    ]
  },
  timeline: {
    section: "timeline",
    path: "timeline",
    label: "Timeline",
    singular: "Event",
    icon: "timeline",
    titleField: "title",
    newItem: { title: "New Event" },
    subtitleField: "storyDate",
    reorderable: true,
    fields: [
      { key: "storyDate", label: "Story date", type: "text", placeholder: "Day 3, 1885, Spring…" },
      { key: "storyTime", label: "Time", type: "text", placeholder: "Dusk, 14:00…" },
      { key: "locationId", label: "Location", type: "location-ref" },
      { key: "sceneId", label: "Scene reference", type: "scene-ref" },
      { key: "characters", label: "Characters involved", type: "text" },
      { key: "tags", label: "Tags", type: "text", placeholder: "comma, separated" },
      { key: "description", label: "Description", type: "textarea", rows: 6 }
    ]
  },
  plotThreads: {
    section: "plotThreads",
    path: "plot-threads",
    label: "Plot Threads",
    singular: "Thread",
    icon: "thread",
    titleField: "name",
    newItem: { name: "New Thread", status: "planned" },
    subtitleField: "status",
    fields: [
      { key: "status", label: "Status", type: "select", options: PLOT_STATUSES },
      { key: "openedSceneId", label: "Opened in", type: "scene-ref" },
      { key: "description", label: "Description", type: "textarea", rows: 5 },
      { key: "relatedCharacters", label: "Related characters", type: "text" },
      { key: "relatedScenes", label: "Related scenes", type: "text" },
      { key: "resolution", label: "Resolution", type: "textarea", rows: 4 },
      { key: "notes", label: "Notes", type: "textarea" }
    ]
  }
};
